import type { RecipeIngredient } from './recipe.js';
import { matchCollettivaDiet } from './collettivaDiet.js';

// T-441 — Diete che ha senso definire come VARIANTE di un piatto cotto (ordinario).
// NON tutto il vocabolario: parenterale/digiuno/idrica/personalizzata sono non-cotti
// o non-per-bocca; aproteica/celiaca/astringente ecc. sono dish standalone a catalogo
// (risolti per nome, T-431). Estendibile. Vedi spec §5.3.
//
// NB (audit avversariale 2026-07-09): `celiaca` NON è variantabile. Nel catalogo
// Bluecoop il senza-glutine è un piatto A SÉ ("2° Celiaco", "Contorno celiaco",
// "Pasta/Pastina celiaca") — non "l'ordinario reso gluten-free" (è tutt'altra
// pasta). Includerlo faceva scambiare quei piatti per varianti dell'ordinario,
// asimmetrico con la famiglia aproteica (che è correttamente standalone). Se un
// domani serve una variante "senza glutine" su una ricetta generica, si riaggiunge.
export const VARIANTABLE_DIET_CODES = [
  'diabetica',
  'iposodica',
  'diabetica_iposodica',
  'senza_latticini',
  'dieta_morbida',
  'ipocalorica',
  'protetta',
] as const;
export type VariantableDietCode = (typeof VARIANTABLE_DIET_CODES)[number];

export function isVariantableDiet(code: string | null | undefined): boolean {
  return !!code && (VARIANTABLE_DIET_CODES as readonly string[]).includes(code);
}

export type OrdinarioCategory = 'PRIMI' | 'SECONDI' | 'CONTORNI';
export interface ParsedOrdinarioLabel {
  category: OrdinarioCategory;
  /** null = ordinario base; altrimenti un codice dieta variantabile. */
  dietCode: string | null;
}

const norm = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Riconosce le etichette della "famiglia ordinario" del catalogo ordini
 * (`1°/2°/Primo/Secondo/Contorno [Ordinario] <dieta>`) e ne estrae categoria +
 * codice dieta. dietCode=null → ordinario base. Ritorna null (intero) se NON è
 * famiglia ordinario o se la dieta non è variantabile → il chiamante userà il
 * direct-match per nome (T-431). Vedi spec §6.1.
 */
export function parseOrdinarioLabel(label: string): ParsedOrdinarioLabel | null {
  const n = norm(label);
  let category: OrdinarioCategory;
  let rest: string;
  if (/^1( |$)/.test(n) || n.startsWith('primo ')) {
    category = 'PRIMI';
    rest = n.replace(/^1 /, '').replace(/^primo /, '');
  } else if (/^2( |$)/.test(n) || n.startsWith('secondo ')) {
    category = 'SECONDI';
    rest = n.replace(/^2 /, '').replace(/^secondo /, '');
  } else if (n.startsWith('cont')) {
    category = 'CONTORNI';
    rest = n.replace(/^contorno /, '').replace(/^cont /, '');
  } else {
    return null;
  }
  rest = rest.replace(/\bordinari[oa]\b/g, '').replace(/\bord\b/g, '').replace(/\s+/g, ' ').trim();
  if (rest === '') return { category, dietCode: null };
  const code = matchCollettivaDiet(rest);
  if (code && isVariantableDiet(code)) return { category, dietCode: code };
  return null;
}

/**
 * Fingerprint deterministico dell'insieme ingredienti (food_id/qty/uom), stabile
 * all'ordine. Usato per rilevare la deriva base→variante (spec §5.4). Non è un
 * hash crittografico: serve solo il confronto di uguaglianza.
 */
export function ingredientsFingerprint(ingredients: RecipeIngredient[]): string {
  return ingredients
    .map((i) => `${i.food_id}:${i.qty ?? ''}:${i.uom ?? ''}`)
    .sort()
    .join('|');
}
